import { useEffect, type ReactNode } from "react";

interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: string;
}

export default function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  width = "max-w-lg",
}: ModalProps) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-10">
      <div
        className="absolute inset-0 bg-foreground-950/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`relative w-full ${width} overflow-hidden rounded-lg border border-background-200 bg-background-50`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h3 className="text-base font-semibold text-foreground-950">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
            aria-label="关闭"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-5">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-3 border-t border-background-200 px-5 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}